<template>
    <ButtonPopup
        :id="id"
        :container-class="[
            'e3-dropdown',
            {'e3-dropdown-popup-open': isOpen}
        ]"
        :button-class="buttonClasses"
        popup-class="e3-dropdown-popup"
        :popper-options="popperOptions"
        :disabled="isDisabled"
        :lazy="lazy"
        @open="togglePopup(true)"
        @close="togglePopup(false)">
        <div class="d-flex align-items-center">
            <slot v-if="hasIcon" name="icon" :open="isOpen"/>
            <span
                v-if="text || hasButtonContent"
                class="e3-dropdown-toggle-text text-left"
                :class="hasButtonContent ? 'text-nowrap' : 'text-truncate'">
                <slot name="button-content" :open="isOpen">{{text}}</slot>
            </span>
            <span
                v-if="showCaret"
                class="e3-dropdown-arrow e3-transition ml-3"
                :class="{ 'e3-rotate-180': isOpen }">
                <ChevronDownIcon class="text-primary"/>
            </span>
        </div>
        <template v-if="hasContent" #popup="{close}">
            <slot name="content" :close="close"/>
        </template>
        <template v-else #popup="{close}">
            <ul>
                <template v-for="(list, listIndex) in itemLists">
                    <li
                        v-for="item in list"
                        :id="item.id"
                        :key="`${listIndex}-${itemKey(item)}`"
                        class="e3-dropdown-item py-1 text-nowrap"
                        :class="{
                            'active': item[keyName] === active,
                            'disabled': item.disabled,
                            'e3-pointer': !item.disabled}"
                        :aria-disabled="item.disabled ? 'true' : null"
                        @click="handleItemClick(item, close)">
                        <slot name="item" v-bind="item">
                            <div class="d-flex align-items-center">
                                <AccountEditOutlineIcon v-if="item.type === 'edit'" size="20" class="mr-1"/>
                                <TrashCanOutlineIcon v-if="item.type === 'delete'" size="20" class="mr-1"/>
                                <span>{{ item.name }}</span>
                                <template v-if="item.type === 'survey-radio'">
                                    <div class="e3-check-demo-items">
                                        <ERadio :id="`${fieldId(item)}-radio`" class="mr-1" value="true"/>
                                        <ERadio :id="`${fieldId(item)}-radio-value`"/>
                                    </div>
                                </template>
                                <template v-if="item.type === 'survey-check'">
                                    <div class="e3-check-demo-items">
                                        <ECheckbox :id="`${fieldId(item)}-check`" class="mr-2"/>
                                        <ECheckbox :id="`${fieldId(item)}-check-value`" value="true" success/>
                                    </div>
                                </template>
                            </div>
                        </slot>
                    </li>
                    <li v-if="listIndex === 0 && extraItems.length" :key="`extra-toggle-${listIndex}`" class="e3-dropdown-item e3-pointer pt-1">
                        <button
                            class="btn btn-link text-muted p-0 d-flex align-items-center text-decoration-none"
                            type="button"
                            @click="toggleExtraItems">
                            <MinusIcon v-show="extraItemsVisible" size="20" class="mr-1 text-muted"/>
                            <PlusIcon v-show="!extraItemsVisible" size="20" class="mr-1 text-muted"/>
                            <span>{{extraItemsBtnText}}</span>
                        </button>
                    </li>
                </template>
            </ul>
        </template>
    </ButtonPopup>
</template>

<script>
import ButtonPopup from '@/common/components/button-popup/ButtonPopup.vue';
import ChevronDownIcon from '@/common/components/svg-icons/ChevronDownIcon.vue';
import AccountEditOutlineIcon from '@/common/components/svg-icons/AccountEditOutlineIcon.vue';
import TrashCanOutlineIcon from '@/common/components/svg-icons/TrashCanOutlineIcon.vue';
import MinusIcon from '@/common/components/svg-icons/MinusIcon.vue';
import PlusIcon from '@/common/components/svg-icons/PlusIcon.vue';

export default {
    name: 'EDropdown',
    props: {
        id: {
            type: [String, Number],
            default: null
        },
        keyName: {
            type: String,
            default: 'id'
        },
        text: {
            type: String,
            default: ''
        },
        extraItemsBtnText: {
            type: String,
            default: ''
        },
        placement: {
            type: String,
            default: 'bottom-start'
        },
        items: {
            type: Array,
            default: () => []
        },
        extraItems: {
            type: Array,
            default: () => []
        },
        active: {
            type: [Number, String],
            default: null
        },
        xOffset: {
            type: Number,
            default: 0
        },
        yOffset: {
            type: Number,
            default: 2
        },
        btnClass: {
            type: String,
            default: ''
        },
        btnSize: {
            type: String
        },
        variant: {
            type: String,
            default: 'primary'
        },
        hasArrow: {
            type: Boolean,
            default: false
        },
        lazy: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isOpen: false,
            extraItemsVisible: false
        };
    },
    computed: {
        buttonClasses() {
            let classList = `${this.btnClass}`;
            if (this.variant === 'primary') {
                classList += ' e3-dropdown-toggle position-relative d-block w-100 text-dark';
            } else {
                classList += ' border-0 bg-transparent';
            }
            if (this.btnSize) {
                classList += ` btn btn-${this.btnSize}`;
            }
            return classList;
        },
        popperOptions() {
            return {
                placement: this.placement,
                modifiers: [
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: ['top', 'right', 'left']
                        }
                    },
                    {
                        name: 'arrow',
                        enabled: this.hasArrow
                    },
                    {
                        name: 'offset',
                        options: {
                            offset: [this.xOffset, this.yOffset]
                        }
                    }
                ]
            };
        },
        itemLists() {
            const lists = [this.items];
            if (this.extraItemsVisible) {
                lists.push(this.extraItems);
            }
            return lists;
        },
        showCaret() {
            return (this.items.length + this.extraItems.length) > 1;
        },
        // TODO: Vue 3 replace $scopedSlots with $slots
        hasContent() {
            return !!this.$scopedSlots['content'];
        },
        // TODO: Vue 3 replace $scopedSlots with $slots
        hasButtonContent() {
            return !!this.$scopedSlots['button-content'];
        },
        // TODO: Vue 3 replace $scopedSlots with $slots
        hasIcon() {
            return !!this.$scopedSlots.icon;
        },
        isDisabled() {
            return !this.hasContent && (this.items.length + this.extraItems.length) <= 1;
        }
    },
    methods: {
        itemKey(item) {
            return item.key || item[this.keyName];
        },
        fieldId(item) {
            return `${this.id || 'dropdown'}-${this.itemKey(item)}`;
        },
        handleItemClick(item, close) {
            if (item.disabled) {
                return;
            }
            if (typeof item.action === 'function' && typeof close === 'function') {
                item.action();
                close();
            }
        },
        togglePopup(visible) {
            this.isOpen = visible;
            this.$emit('toggle', visible);
        },
        toggleExtraItems() {
            this.extraItemsVisible = !this.extraItemsVisible;
        }
    },
    components: {
        ButtonPopup,
        ChevronDownIcon,
        AccountEditOutlineIcon,
        TrashCanOutlineIcon,
        MinusIcon,
        PlusIcon
    }
};
</script>